import { useState, useEffect } from 'react'
import { flagsApi, type Flag } from '../utils/api'

type Toast = { type: 'success' | 'error'; message: string } | null

export default function Flags() {
  const [flags, setFlags] = useState<Flag[]>([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<Toast>(null)

  // New flag form
  const [newKey, setNewKey]     = useState('')
  const [newValue, setNewValue] = useState('')
  const [newDesc, setNewDesc]   = useState('')
  const [adding, setAdding]     = useState(false)

  // Inline edit
  const [editingKey, setEditingKey]     = useState<string | null>(null)
  const [editValue, setEditValue]       = useState('')
  const [editDesc, setEditDesc]         = useState('')
  const [saving, setSaving]             = useState(false)
  const [deletingKey, setDeletingKey]   = useState<string | null>(null)

  function showToast(type: 'success' | 'error', message: string) {
    setToast({ type, message })
    setTimeout(() => setToast(null), 4000)
  }

  async function load() {
    setLoading(true)
    try {
      const { flags: list } = await flagsApi.list()
      setFlags(list)
    } catch (err) {
      showToast('error', `Load failed: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!newKey.trim() || !newValue.trim()) return
    setAdding(true)
    try {
      await flagsApi.save({ key: newKey.trim(), value: newValue.trim(), description: newDesc.trim() || undefined })
      showToast('success', '✓ Flag saved')
      setNewKey(''); setNewValue(''); setNewDesc('')
      await load()
    } catch (err) {
      showToast('error', `Failed: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setAdding(false)
    }
  }

  async function handleSaveEdit(key: string) {
    setSaving(true)
    try {
      await flagsApi.save({ key, value: editValue.trim(), description: editDesc.trim() || undefined })
      showToast('success', '✓ Flag updated')
      setEditingKey(null)
      await load()
    } catch (err) {
      showToast('error', `Save failed: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(key: string) {
    if (!confirm(`Delete flag "${key}"?`)) return
    setDeletingKey(key)
    try {
      await flagsApi.delete(key)
      showToast('success', '✓ Flag deleted')
      await load()
    } catch (err) {
      showToast('error', `Delete failed: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setDeletingKey(null)
    }
  }

  function getBooleanBadge(value: string) {
    const v = value.toLowerCase()
    if (v === 'true' || v === '1' || v === 'on' || v === 'enabled') {
      return <span className="badge badge-success">✓ {value}</span>
    }
    if (v === 'false' || v === '0' || v === 'off' || v === 'disabled') {
      return <span className="badge badge-warning">○ {value}</span>
    }
    return <span className="badge badge-accent">{value}</span>
  }

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: '1.4rem' }}>Feature Flags</h1>
          <p style={{ fontSize: '0.82rem', marginTop: 4 }}>
            Global runtime configuration variables stored in D1
          </p>
        </div>
      </div>

      {/* Add form */}
      <div className="glass-card" style={{ padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
          New Flag
        </div>
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: '0 0 180px' }}>
            <label className="form-label" htmlFor="input-flag-key">Key *</label>
            <input
              id="input-flag-key"
              className="form-input"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value.replace(/\s/g, '_').toUpperCase())}
              placeholder="FEATURE_KEY"
              style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}
              required
            />
          </div>
          <div className="form-group" style={{ flex: '0 0 160px' }}>
            <label className="form-label" htmlFor="input-flag-value">Value *</label>
            <input
              id="input-flag-value"
              className="form-input"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="true / 42 / text"
              style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}
              required
            />
          </div>
          <div className="form-group" style={{ flex: 1, minWidth: 180 }}>
            <label className="form-label" htmlFor="input-flag-desc">Description</label>
            <input
              id="input-flag-desc"
              className="form-input"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="What does this flag control?"
            />
          </div>
          <button
            id="btn-add-flag"
            type="submit"
            className="btn btn-primary"
            disabled={adding}
            style={{ flexShrink: 0 }}
          >
            {adding ? '…' : '+ Add Flag'}
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="glass-card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 24 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8 }} />
            ))}
          </div>
        ) : flags.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🚩</div>
            <p>No feature flags configured yet</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Description</th>
                <th>Updated</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((flag) => (
                <tr key={flag.key}>
                  <td>
                    <code style={{ fontSize: '0.82rem', color: 'var(--accent-hover)', fontWeight: 600 }}>
                      {flag.key}
                    </code>
                  </td>
                  <td>
                    {editingKey === flag.key ? (
                      <input
                        id={`input-edit-value-${flag.key}`}
                        className="form-input"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', padding: '4px 10px', width: 160 }}
                        autoFocus
                      />
                    ) : (
                      getBooleanBadge(flag.value)
                    )}
                  </td>
                  <td>
                    {editingKey === flag.key ? (
                      <input
                        id={`input-edit-desc-${flag.key}`}
                        className="form-input"
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        style={{ fontSize: '0.82rem', padding: '4px 10px' }}
                        placeholder="Description"
                      />
                    ) : (
                      <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                        {flag.description || '—'}
                      </span>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {new Date(flag.updated_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {editingKey === flag.key ? (
                        <>
                          <button
                            id={`btn-save-flag-${flag.key}`}
                            className="btn btn-primary btn-sm"
                            onClick={() => handleSaveEdit(flag.key)}
                            disabled={saving}
                          >
                            {saving ? '…' : '✓ Save'}
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditingKey(null)}>
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            id={`btn-edit-flag-${flag.key}`}
                            className="btn btn-ghost btn-sm"
                            onClick={() => { setEditingKey(flag.key); setEditValue(flag.value); setEditDesc(flag.description ?? '') }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            id={`btn-delete-flag-${flag.key}`}
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(flag.key)}
                            disabled={deletingKey === flag.key}
                          >
                            {deletingKey === flag.key ? '…' : '🗑'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`} role="alert" aria-live="polite">
          {toast.message}
        </div>
      )}
    </div>
  )
}
